import { ArrowUpRight, Info } from "lucide-react";
import { GOOGLE_FORM_URL, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

const WEEKS = [
  {
    title: "Week 1 — Settling In & New Year",
    days: [
      { date: "Mon 29 Dec", plan: "Salon Day", location: "Circle Mall", note: "Full day for salon appointments.", budget: "₦120,000–₦180,000" },
      { date: "Tue 30 Dec", plan: "Rest Day + Local Lounge", note: "Rest and a short local outing.", budget: "₦5,000–₦15,000 per person" },
      { date: "Wed 31 Dec", plan: "New Year Crossover Service", location: "City of David Church", budget: "Free admission; other expenses may apply" },
      { date: "Thu 1 Jan", plan: "Sol Beach", location: "Elegushi", note: "Daytime beach outing.", budget: "₦40,000–₦60,000" },
      { date: "Fri 2 Jan", plan: "Jakande Art Market", note: "Morning visit.", budget: "₦20,000–₦40,000" },
      { date: "Sat 3 Jan", plan: "Sky Mall Fun Day", note: "Cinema and arcade.", budget: "₦50,000–₦80,000" },
      { date: "Sun 4 Jan", plan: "Church + Chill Lounge", note: "Church and relaxed food outing at Bites & Chill.", budget: "₦5,000–₦15,000 per person" },
    ],
  },
  {
    title: "Week 2 — Shopping, Nature & Water Fun",
    days: [
      { date: "Mon 5 Jan", plan: "Shopping", location: "Ochra Mall", budget: "₦80,000–₦150,000" },
      { date: "Tue 6 Jan", plan: "Lekki Conservation Centre", note: "Early-morning visit and return by noon.", budget: "₦30,000–₦40,000" },
      { date: "Wed 7 Jan", plan: "Balogun Market", note: "Morning visit with a shopping list.", budget: "₦50,000–₦100,000" },
      { date: "Thu 8 Jan", plan: "Giwa Gardens Water Park", note: "Full family day.", budget: "₦60,000–₦90,000" },
      { date: "Fri 9 Jan", plan: "Nightlife Split", note: "Parents and Oge visit Bogobiri for live music.", budget: "₦20,000 per person" },
      { date: "Sat 10 Jan", plan: "Ikoyi to Lekki Scenic Day", note: "Scenic drive, Eric Kayser and Mike Adenuga Centre.", budget: "₦40,000–₦60,000" },
      { date: "Sun 11 Jan", plan: "Fela Kuti Centre + Airport", note: "Morning visit, then Oge departs.", budget: "₦10,000–₦15,000 per person" },
    ],
  },
  {
    title: "Week 3 — A Calmer Family Rhythm",
    days: [
      { date: "Mon 12 Jan", plan: "Rest + Lounge", location: "Café Neo, Lekki Phase 1", budget: "₦5,000–₦15,000 per person" },
      { date: "Tue 13 Jan", plan: "Upbeat Trampoline Park", note: "Family activity.", budget: "₦40,000–₦60,000" },
      { date: "Wed 14 Jan", plan: "Full Rest Day", note: "No scheduled activities." },
      { date: "Thu 15 Jan", plan: "RF Gardens", note: "Adult live traditional music; booking ahead may be necessary.", budget: "From ₦40,000" },
      { date: "Fri 16 Jan", plan: "Investment Viewing Day", location: "Lekki / Ajah", budget: "Free–₦50,000" },
      { date: "Sat 17 Jan", plan: "Art Market & Exhibitions", budget: "₦20,000–₦40,000" },
      { date: "Sun 18 Jan", plan: "Kyma Beach", location: "Elegushi", budget: "₦50,000–₦70,000" },
    ],
  },
  {
    title: "Final Days",
    days: [
      { date: "Mon 19 Jan", plan: "Comedy Night", budget: "₦10,000–₦20,000 per person" },
      { date: "Tue 20 Jan", plan: "Packing & Souvenir Day", note: "Prepare for departure and buy final souvenirs." },
      { date: "Wed 21 Jan", plan: "Final Food Day", note: "Favourite local food and a relaxed final outing." },
      { date: "Thu 22 Jan", plan: "Goodbye Lagos", note: "Rest, reflect and prepare for departure." },
    ],
  },
];

export default function CaseStudyPage() {
  return (
    <div className="bg-jawa-ivory" data-testid="case-study-page">
      <Nav />
      <Seo
        title="A Lagos Christmas, Planned Around Family Life | JÀWÀ Africa"
        description="An illustrative JÀWÀ itinerary case study: the Smith family's traffic-aware Lagos Christmas planner, 29 December – 22 January, based in Osapa London."
      />
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-jawa-coral">Itinerary case study</p>
          <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-jawa-espresso sm:text-5xl">
            A Lagos Christmas, Planned Around Family Life
          </h1>
          <p className="mt-3 text-sm uppercase tracking-[0.25em] text-jawa-turquoise">
            The Smith Family · A traffic-aware family planner
          </p>
          <div className="mt-6 flex items-start gap-3 border border-jawa-gold/50 bg-jawa-gold/5 p-5" data-testid="illustrative-notice">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-jawa-gold" />
            <p className="text-sm leading-relaxed text-jawa-espresso/70">
              This is an <strong>illustrative, historical itinerary</strong> — a demonstration of how JÀWÀ plans a
              family stay (proposed dates 29 December–22 January, based in Osapa London), balancing traffic,
              rest, activities, shopping and different family members' interests. It is not an upcoming trip,
              and venue names, prices, opening hours and programmes must be reconfirmed before any future trip.
            </p>
          </div>
        </FadeUp>

        {WEEKS.map((w, wi) => (
          <FadeUp key={w.title} className="mt-14" data-testid={`week-${wi + 1}`}>
            <h2 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso">{w.title}</h2>
            <div className="mt-5 divide-y divide-jawa-stone border border-jawa-espresso/10 bg-white">
              {w.days.map((d) => (
                <div key={d.date} className="grid gap-2 p-5 sm:grid-cols-[130px_1fr_auto] sm:items-baseline sm:gap-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-jawa-coral">{d.date}</p>
                  <div>
                    <p className="font-medium text-jawa-espresso">
                      {d.plan}
                      {d.location && <span className="text-jawa-espresso/55"> · {d.location}</span>}
                    </p>
                    {d.note && <p className="mt-1 text-sm text-jawa-espresso/60">{d.note}</p>}
                  </div>
                  {d.budget && (
                    <p className="text-xs text-jawa-espresso/55 sm:text-right">
                      <span className="uppercase tracking-wider text-jawa-espresso/40">Historical budget</span>
                      <br />
                      {d.budget}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </FadeUp>
        ))}

        <FadeUp className="mt-16">
          <p className="font-serif text-2xl italic leading-snug text-jawa-espresso" data-testid="case-study-closing">
            “A good itinerary leaves room for real life.”
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="case-study-primary-cta"
              className="inline-flex items-center gap-2 bg-jawa-coral px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-espresso"
            >
              Plan Your Bespoke Family Itinerary <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href={waLink("Hello JÀWÀ — I'd like to talk about a family itinerary.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="case-study-secondary-cta"
              className="inline-flex items-center gap-2 border border-jawa-espresso/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso transition-colors hover:border-jawa-turquoise hover:text-jawa-turquoise"
            >
              Talk to JÀWÀ
            </a>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-jawa-espresso/50">
            Naira figures are historical estimates from the illustrated period; any conversions are indicative
            and made with JÀWÀ's configured rates. All details require reconfirmation before a future trip.
          </p>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
